import { useCallback, useEffect, useMemo, useState } from "react";
import { backupReminder, loadBackupState, markBackupSaved, snoozeBackupReminder, type BackupState } from "../core/backup";
import { listExercises, type Exercise, type ExerciseFilter } from "../core/exercises";
import { idbBackend } from "../core/idb";
import { applySeeds } from "../core/seed";
import { recordTimeline } from "../core/progress";
import { deleteSession, listSessions, saveSession, sessionTemplate, type Session } from "../core/sessions";
import { openStorage, type Backend } from "../core/storage";
import { exportBackup, fileName } from "../core/transfer";
import { foldText } from "../core/exercises";
import {
  copyName, deleteTemplate, duplicateTemplate, lastDoneByTemplate, listTemplates, saveTemplate, type Template,
} from "../core/templates";
import { settleSensorSets } from "../core/repSensor";
import { finishWorkout, repeatWorkout, startWorkout, type Workout } from "../core/workout";
import { platform, saveJson } from "../platform";
import { goBack, useBack } from "./back";
import { DataPage } from "./DataPage";
import { ExerciseEditor } from "./ExerciseEditor";
import { ExercisePage } from "./ExercisePage";
import { ExercisesTab } from "./ExercisesTab";
import { RecordScreen } from "./RecordScreen";
import { HistoryTab, SessionPage } from "./HistoryTab";
import { SessionEditor } from "./SessionEditor";
import { TemplateEditor } from "./TemplateEditor";
import { WorkoutScreen } from "./WorkoutScreen";
import { WorkoutSummary } from "./WorkoutSummary";
import { WorkoutTab } from "./WorkoutTab";
import { formatClock, formatDate, formatDuration, t } from "./texts";
import { useRepSensor } from "./useRepSensor";
import { useWorkout } from "./useWorkout";

type Tab = "history" | "workout" | "exercises";
/** What the Übungen tab shows on top of its list. */
type ExerciseView = { kind: "list" } | { kind: "page"; id: string } | { kind: "edit"; id?: string; back: ExerciseView };
/** What the Workout tab shows: the start screen, the template editor (no id: a new template) or export and import. */
type WorkoutView = { kind: "start" } | { kind: "edit"; id?: string } | { kind: "data" };
/** What the Verlauf tab shows: the list, a session, the session editor, or the hidden motion recording (todo 7). */
type HistoryView = { kind: "list" } | { kind: "session"; id: string } | { kind: "edit"; id: string } | { kind: "record" };

interface Data {
  exercises: Exercise[];
  templates: Template[];
  sessions: Session[];
  lastDone: Map<string, number>;
  backup: BackupState;
}

async function load(db: Backend): Promise<Data> {
  const [exercises, templates, sessions, backup] = await Promise.all([listExercises(db), listTemplates(db), listSessions(db), loadBackupState(db)]);
  return { exercises, templates, sessions, lastDone: lastDoneByTemplate(sessions), backup };
}

export function App() {
  const [db, setDb] = useState<Backend>();
  const [error, setError] = useState<string>();
  const [data, setData] = useState<Data>({ exercises: [], templates: [], sessions: [], lastDone: new Map(), backup: {} });
  const [tab, setTab] = useState<Tab>("workout");
  const [view, setView] = useState<ExerciseView>({ kind: "list" });
  const [workoutView, setWorkoutView] = useState<WorkoutView>({ kind: "start" });
  const [historyView, setHistoryView] = useState<HistoryView>({ kind: "list" });
  const [filter, setFilter] = useState<ExerciseFilter>({});
  const [summary, setSummary] = useState<{ session: Session; templateUpdated: boolean }>();
  /** a new version is downloaded (web app): switches to it */
  const [update, setUpdate] = useState<() => void>();
  const run = useWorkout(db, data.exercises);
  const sensor = useRepSensor(db, run.workout, data.exercises, run.apply);
  // records are derived from all sessions; recomputed only when the data is reloaded
  const timeline = useMemo(() => recordTimeline(data.sessions, data.exercises), [data.sessions, data.exercises]);
  // the lowest back layer: other tab → Workout tab; on the Workout tab the app goes into the background
  useBack(() => (tab === "workout" ? platform.minimize() : setTab("workout")));
  useEffect(() => platform.onBackButton(goBack), []);
  useEffect(() => platform.watchUpdates((reload) => setUpdate(() => reload)), []);

  const reload = useCallback((backend: Backend) => load(backend).then(setData), []);
  useEffect(() => {
    openStorage(idbBackend())
      .then(async (backend) => {
        await applySeeds(backend);
        await reload(backend);
        setDb(backend);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [reload]);

  if (error) return <main className="page"><h1>{t.appName}</h1><p className="error">{t.storageError} {error}</p></main>;
  if (!db) return <main className="page"><p>{t.loading}</p></main>;

  const { exercises, templates } = data;
  const changed = () => reload(db);
  const page = view.kind === "page" ? exercises.find((e) => e.id === view.id) : undefined;
  const workout = run.workout;
  // the template editor is full screen like Strong's: switching tabs would lose the draft
  const editingTemplate = tab === "workout" && workoutView.kind === "edit" && !workout && !summary;
  const shownSession = historyView.kind === "session" || historyView.kind === "edit"
    ? data.sessions.find((x) => x.id === historyView.id) : undefined;
  const recording = tab === "history" && historyView.kind === "record";
  const editing = editingTemplate || recording || (tab === "history" && historyView.kind === "edit" && !!shownSession);

  const begin = (w: Workout) => {
    run.start(w);
    setWorkoutView({ kind: "start" });
    setTab("workout");
  };
  const start = (templateId?: string) => {
    const template = templateId ? templates.find((x) => x.id === templateId) : undefined;
    begin(startWorkout({ template, exercises, name: t.run.emptyName, now: Date.now() }));
  };
  /** "Als Vorlage speichern": stored right away under the session's name (a free "(Kopie)" name if it is taken). */
  const saveAsTemplate = async (s: Session) => {
    const names = templates.map((x) => x.name);
    const name = names.some((n) => foldText(n) === foldText(s.name)) ? copyName(s.name, names, t.template.copyWord) : s.name;
    const r = await saveTemplate(db, sessionTemplate(s, exercises, name), exercises);
    await changed();
    return r.ok ? r.template.name : name;
  };
  const finish = async (tplUpdate: Template | undefined) => {
    if (!workout) return;
    const session = finishWorkout(workout, Date.now());
    await saveSession(db, session);
    if (tplUpdate) await db.put("templates", tplUpdate);
    await settle(workout, true);
    run.end();
    setSummary({ session, templateUpdated: !!tplUpdate });
    await changed();
  };

  /** The sensor sets' recordings get their final reps (or go, when the workout is discarded); the profiles learn. */
  const settle = async (w: Workout, kept: boolean) => {
    try {
      await settleSensorSets(db, w, kept);
      await sensor.reload();
    } catch (e) {
      console.error("settling the sensor sets failed", e);
    }
  };
  const discard = async () => {
    if (workout) await settle(workout, false);
    run.end();
  };

  /** The backup reminder's "Backup speichern": the same file as on the Daten page. */
  const saveBackup = async () => {
    const file = await exportBackup(db, Date.now());
    try {
      if (!(await saveJson(fileName(file.kind, file.exportedAt), file))) return;
    } catch (e) {
      return console.error("saving the backup failed", e);
    }
    await markBackupSaved(db, Date.now());
    await changed();
  };
  const reminder = backupReminder(data.sessions, data.backup);

  let content;
  if (recording) {
    content = <RecordScreen db={db} exercises={exercises} onClose={() => setHistoryView({ kind: "list" })} onLearned={sensor.reload} />;
  } else if (tab === "history" && historyView.kind === "edit" && shownSession) {
    content = (
      <SessionEditor key={shownSession.id} db={db} session={shownSession} exercises={exercises} onCreatedExercise={changed}
        onCancel={() => setHistoryView({ kind: "session", id: shownSession.id })}
        onSaved={async () => { await changed(); setHistoryView({ kind: "session", id: shownSession.id }); }} />
    );
  } else if (tab === "history" && shownSession) {
    content = (
      <SessionPage key={shownSession.id} session={shownSession} exercises={exercises} records={timeline.get(shownSession.id) ?? []}
        busy={!!workout}
        onBack={() => setHistoryView({ kind: "list" })}
        onEdit={() => setHistoryView({ kind: "edit", id: shownSession.id })}
        onSaveAsTemplate={() => saveAsTemplate(shownSession)}
        onRepeat={() => { setHistoryView({ kind: "list" }); begin(repeatWorkout(shownSession, exercises, Date.now())); }}
        onDelete={async () => { await deleteSession(db, shownSession.id); setHistoryView({ kind: "list" }); await changed(); }} />
    );
  } else if (tab === "history") {
    content = <HistoryTab sessions={data.sessions} exercises={exercises} timeline={timeline} onOpen={(id) => setHistoryView({ kind: "session", id })}
      onSecret={() => void platform.motion().then((m) => { if (m) setHistoryView({ kind: "record" }); })} />;
  } else if (tab === "workout" && summary) {
    content = <WorkoutSummary session={summary.session} templateUpdated={summary.templateUpdated} exercises={exercises}
      records={timeline.get(summary.session.id) ?? []}
      onClose={() => setSummary(undefined)} />;
  } else if (tab === "workout" && workout) {
    content = (
      <WorkoutScreen db={db} workout={workout} now={run.now} exercises={exercises} templates={templates} sessions={data.sessions}
        apply={run.apply} onDataChanged={changed} onFinish={finish} onDiscard={discard}
        sensor={sensor.settings} onSensor={sensor.setOn} />
    );
  } else if (editingTemplate && workoutView.kind === "edit") {
    content = (
      <TemplateEditor
        key={workoutView.id ?? "new"}
        db={db}
        exercises={exercises}
        sessions={data.sessions}
        initial={workoutView.id ? templates.find((x) => x.id === workoutView.id) : undefined}
        onCreatedExercise={changed}
        onCancel={() => setWorkoutView({ kind: "start" })}
        onSaved={async () => { await changed(); setWorkoutView({ kind: "start" }); }}
      />
    );
  } else if (tab === "workout" && workoutView.kind === "data") {
    content = (
      <DataPage db={db} exercises={exercises} templates={templates} busy={!!workout}
        onBack={() => setWorkoutView({ kind: "start" })} onChanged={changed} />
    );
  } else if (tab === "workout") {
    content = (
      <WorkoutTab templates={templates} exercises={exercises} lastDone={data.lastDone}
        backupReminder={reminder === undefined ? undefined : t.data.reminder(reminder, data.backup.savedAt === undefined ? undefined : formatDate(data.backup.savedAt))}
        onSaveBackup={saveBackup}
        onSnoozeBackup={async () => { await snoozeBackupReminder(db, Date.now()); await changed(); }}
        onStart={start}
        onCreate={() => setWorkoutView({ kind: "edit" })}
        onData={() => setWorkoutView({ kind: "data" })}
        onEdit={(id) => setWorkoutView({ kind: "edit", id })}
        onDuplicate={async (id) => { await duplicateTemplate(db, id, t.template.copyWord); await changed(); }}
        onDelete={async (id) => { await deleteTemplate(db, id); await changed(); }} />
    );
  } else if (view.kind === "edit") {
    content = (
      <ExerciseEditor
        db={db}
        exercises={exercises}
        initial={view.id ? exercises.find((e) => e.id === view.id) : undefined}
        onCancel={() => setView(view.back)}
        onSaved={async (e) => { await changed(); setView({ kind: "page", id: e.id }); }}
      />
    );
  } else if (page) {
    content = (
      <ExercisePage db={db} exercise={page} sessions={data.sessions} timeline={timeline} onBack={() => setView({ kind: "list" })}
        onEdit={() => setView({ kind: "edit", id: page.id, back: view })}
        onChanged={changed} onRemoved={async () => { await changed(); setView({ kind: "list" }); }} />
    );
  } else {
    content = <ExercisesTab exercises={exercises} filter={filter} onFilter={setFilter}
      onOpen={(id) => setView({ kind: "page", id })} onCreate={() => setView({ kind: "edit", back: { kind: "list" } })} />;
  }

  return (
    <>
      <main className={editing ? "page" : "page with-tabs"}>{content}</main>
      {(update || run.asleepMs > 0 || sensor.notice) && (
        <div className="banners" role="status">
          {update && (
            <div className="banner">
              <p>{t.app.updateReady}</p>
              <button className="primary" onClick={update}>{t.app.reload}</button>
            </div>
          )}
          {sensor.notice && (
            <div className="banner">
              <p>{sensor.notice}</p>
              <button className="secondary" onClick={sensor.dismissNotice}>{t.app.ok}</button>
            </div>
          )}
          {run.asleepMs > 0 && (
            <div className="banner">
              <p>{t.app.asleep(formatAsleep(run.asleepMs))}</p>
              <button className="secondary" onClick={run.dismissAsleep}>{t.app.ok}</button>
            </div>
          )}
        </div>
      )}
      {workout && tab !== "workout" && !recording && (
        <button className="mini-bar" onClick={() => setTab("workout")}>
          <span>{t.run.running} · {formatClock(run.now - workout.startedAt)}</span>
          {workout.rest && <span>{t.run.restOf(formatDuration(Math.ceil(Math.max(0, workout.rest.endsAt - run.now) / 1000)))}</span>}
          <span aria-hidden>›</span>
        </button>
      )}
      {!editing && <nav className="tabbar">
        {(["history", "workout", "exercises"] as const).map((k) => (
          <button key={k} className={k === tab ? "active" : ""}
            onClick={() => {
              if (k === tab && k === "exercises") setView({ kind: "list" });
              if (k === tab && k === "history") setHistoryView({ kind: "list" });
              setTab(k);
            }}>
            {t.tabs[k]}
          </button>
        ))}
      </nav>}
    </>
  );
}

const formatAsleep = (ms: number) => {
  const sec = Math.round(ms / 1000);
  return sec < 60 ? `${sec} s` : `${formatDuration(sec)} min`;
};
